/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the React application.
 * Handles user state, login/logout, and token management.
 *
 * Usage:
 *   import { useAuth } from '../contexts/AuthContext';
 *   const { user, isAuthenticated, isSubscriber, login, logout } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import logger from '../utils/logger';

// Create context
const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  /**
   * Initialize auth state on mount
   * Checks if user has valid tokens and fetches current user data
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        logger.log('🔐 Initializing auth state...');

        // Check if user is already authenticated (has tokens)
        if (authService.isAuthenticated()) {
          // Fetch current user data from server
          const result = await authService.getCurrentUser();

          if (result.success) {
            setUser(result.user);
            logger.log('✅ User authenticated:', result.user.email);
          } else {
            // Token invalid or expired, clear auth data
            logger.log('⚠️ Token invalid, clearing auth data');
            authService.clearAuthData();
            setUser(null);
          }
        } else {
          logger.log('📋 No auth tokens found');
        }
      } catch (error) {
        logger.error('❌ Auth initialization error:', error);
        authService.clearAuthData();
        setUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initAuth();
  }, []);

  /**
   * Sign up a new user
   */
  const signup = useCallback(async (email, password, firstName, lastName) => {
    try {
      const result = await authService.signup(email, password, firstName, lastName);

      if (result.success) {
        setUser(result.user);
        logger.log('✅ Signup successful');
        return {
          success: true,
          requiresEmailVerification: result.requiresEmailVerification,
        };
      } else {
        return {
          success: false,
          error: result.error,
        };
      }
    } catch (error) {
      logger.error('❌ Signup error:', error);
      return {
        success: false,
        error: error.message || 'Signup failed',
      };
    }
  }, []);

  /**
   * Log in existing user
   */
  const login = useCallback(async (email, password) => {
    try {
      const result = await authService.login(email, password);

      if (result.success) {
        setUser(result.user);
        logger.log('✅ Login successful');
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error,
        };
      }
    } catch (error) {
      logger.error('❌ Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed',
      };
    }
  }, []);

  /**
   * Log out current user
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setUser(null);
      logger.log('✅ Logout successful');
      return { success: true };
    } catch (error) {
      logger.error('❌ Logout error:', error);
      // Still clear local state even if server call fails
      setUser(null);
      return { success: true };
    }
  }, []);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (updates) => {
    try {
      const result = await authService.updateProfile(updates);

      if (result.success) {
        setUser(result.user);
        logger.log('✅ Profile updated');
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error,
        };
      }
    } catch (error) {
      logger.error('❌ Profile update error:', error);
      return {
        success: false,
        error: error.message || 'Profile update failed',
      };
    }
  }, []);

  /**
   * Refresh current user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const result = await authService.getCurrentUser();

      if (result.success) {
        setUser(result.user);
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error,
        };
      }
    } catch (error) {
      logger.error('❌ Refresh user error:', error);
      return {
        success: false,
        error: error.message || 'Failed to refresh user',
      };
    }
  }, []);

  /**
   * Request password reset
   */
  const requestPasswordReset = useCallback(async (email) => {
    try {
      const result = await authService.requestPasswordReset(email);
      return result;
    } catch (error) {
      logger.error('❌ Password reset request error:', error);
      return {
        success: false,
        error: error.message || 'Password reset request failed',
      };
    }
  }, []);

  /**
   * Reset password with token
   */
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const result = await authService.resetPassword(token, newPassword);
      return result;
    } catch (error) {
      logger.error('❌ Password reset error:', error);
      return {
        success: false,
        error: error.message || 'Password reset failed',
      };
    }
  }, []);

  /**
   * Verify email with token
   */
  const verifyEmail = useCallback(
    async (token) => {
      try {
        const result = await authService.verifyEmail(token);

        if (result.success && user) {
          // Refresh user data to get updated email_verified status
          await refreshUser();
        }

        return result;
      } catch (error) {
        logger.error('❌ Email verification error:', error);
        return {
          success: false,
          error: error.message || 'Email verification failed',
        };
      }
    },
    [user, refreshUser]
  );

  // Computed values
  const isAuthenticated = !!user;
  const isSubscriber =
    user?.subscription_status === 'active' || user?.subscription_status === 'trialing';
  const subscriptionTier = user?.subscription_tier || null;
  const isEmailVerified = user?.email_verified === 1;

  // Context value
  const value = {
    // State
    user,
    loading,
    initialized,

    // Computed
    isAuthenticated,
    isSubscriber,
    subscriptionTier,
    isEmailVerified,

    // Methods
    signup,
    login,
    logout,
    updateProfile,
    refreshUser,
    requestPasswordReset,
    resetPassword,
    verifyEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
